import { useCallback, useMemo } from 'react';

import { useDayJs } from '@common/hooks';

import { TEASING_PRODUCT_IDS } from '../constant/teasing';
import { useProductOptionContext } from '../context/ProductOptionProvider';
import { buildInitialState } from '../core/context-initial-state-builder.core';
import { createAutoSelectedItemCard } from '../core/usecase/auto-selection.core';
import { createInitialItemCardPayload } from '../core/usecase/initial-selection.core';
import { useConfirmButton } from '../hook/confirm-button/useConfirmButton';
import { useShippingFilter } from '../hook/filter/useShippingFilter';
import { useItemCards } from '../hook/item-card/useItemCards';
import { useNoticeText } from '../hook/notice-text/useNoticeText';
import { useAdditionalOptions } from '../hook/option/useAdditionalOptions';
import { useCustomInput } from '../hook/option/useCustomInput';
import { useMainOptions } from '../hook/option/useMainOptions';
import { usePriceSummary } from '../hook/price-summary/usePriceSummary';
import { useCountdown } from '../hook/useCountdown';
import { useIsAuthenticated } from '../query/userAuthenticated';
import { type OptionInfo, type ProductOptionProps } from '../type/common.type';
import { reportProductOptionError } from '../util/executors/error-log-send-executor';
import { productMetadataBuilder } from '../util/transformers/product-metadata-transformer';

export const useProductOptionService = ({
  context,
  config = {},
  onClose,
  initialSelectedOption,
}: ProductOptionProps) => {
  const shouldCheckLogin = context.is_logged_in === null;

  const { isLoggedIn } = useIsAuthenticated({ enabled: shouldCheckLogin });

  const mergedContext = useMemo(
    () => ({
      ...context,
      is_logged_in: shouldCheckLogin ? isLoggedIn : context.is_logged_in,
    }),
    [context, isLoggedIn, shouldCheckLogin],
  );

  const { state, actions } = useProductOptionContext();

  const productMetadata = productMetadataBuilder(state.option.info, config);

  const dayjs = useDayJs();
  const scheduledSaleDate = productMetadata.catalog_product?.scheduled_sale_date;
  const { isTimePassed: isTimePassedTeasing } = useCountdown({ targetTimestamp: scheduledSaleDate });
  const formattedDate = dayjs(scheduledSaleDate).format('M월 D일 HH:mm');

  const disabledAutoAddItemCard = !!scheduledSaleDate;
  const hiddenRestockNotiButton = Boolean(TEASING_PRODUCT_IDS.has(productMetadata.catalog_product?.id ?? ''));

  const customInput = useCustomInput({
    options: state.option.info?.catalog_product.custom_input_option_list || [],
    config,
  });

  const additionalOptions = useAdditionalOptions({ config });

  const mainOptions = useMainOptions({
    context: mergedContext,
    config,
    hiddenRestockNotiButton,
  });

  const itemCards = useItemCards({ config });

  const noticeText = useNoticeText({ config });

  const priceSummary = usePriceSummary({ config });

  const confirmButton = useConfirmButton({
    finalPrice: priceSummary.priceSummary.final_price,
    context: mergedContext,
    config,
    onClose,
  });

  const shippingFilter = useShippingFilter({ option_dropdown_list: mainOptions.option_dropdown_list, config });

  const initialize = useCallback(
    (option_info: OptionInfo) => {
      try {
        // 1. 초기화 여부 판단
        // state.option.info가 없거나 상품 ID가 다르면 초기 진입으로 간주
        const currentProductId = state.option.info?.catalog_product.id;
        const newProductId = option_info.catalog_product.id;
        const isNewProduct = currentProductId !== newProductId;

        if (isNewProduct) {
          // [Case 1] 완전히 새로운 상품 진입 -> 상태 전체 초기화
          actions.initializeOption(buildInitialState(option_info, config));
        } else {
          // [Case 2] 같은 상품 재진입 -> 뷰만 리셋 (기존 선택 유지 여부는 기획에 따라 다름)
          actions.resetOptionView({
            enabledShowOnlyZigzinOption: config?.enabledShowOnlyZigzinOption ?? false,
          });
        }

        // 2. 아이템 자동 선택 처리
        // 주의: 초기화 직후에는 state가 아직 업데이트되지 않았을 수 있으므로,
        // 필요한 정보(option_info)를 인자로 넘겨서 처리해야 함.
        if (initialSelectedOption) {
          // [Case A] 외부에서 주입된 초기 선택 옵션이 있는 경우 (장바구니 등)
          const payload = createInitialItemCardPayload({
            initialOption: initialSelectedOption,
            optionInfo: option_info,
            itemCards: { list: [], total_count: 0 }, // 초기화이므로 빈 상태
            productMetadata,
            config,
          });
          if (payload) {
            actions.addItemCard(payload);
            return; // 초기 선택이 있으면 자동 선택 로직은 건너뜀
          }
        }

        if (!disabledAutoAddItemCard) {
          // [Case B] 자동 선택 조건 만족 시 (단일 옵션 등)
          const payload = createAutoSelectedItemCard({
            optionInfo: option_info,
            productMetadata,
            config,
          });
          if (payload) actions.addItemCard(payload);
        }
      } catch (error) {
        reportProductOptionError({ message: '옵션 초기화에 실패했습니다.', originalError: error });
      }
    },
    [state.option.info, actions, config, disabledAutoAddItemCard, initialSelectedOption, productMetadata],
  );

  const handleClose = useCallback(() => {
    actions.resetOptionView({
      enabledShowOnlyZigzinOption: config?.enabledShowOnlyZigzinOption ?? false,
    });
    onClose?.();
  }, [
    actions,
    onClose,
    state.option.info?.catalog_product.custom_input_option_list,
    config?.enabledShowOnlyZigzinOption,
  ]);

  return {
    initialize,
    handleClose,

    // 직진 배송 알림 팝업
    restockNotificationPopup: {
      status: state.ui.restock_notification_status,
      actions: {
        // 재입고 알림 팝업 닫기
        onClose: () => actions.updateRestockNotificationStatus(null),
      },
    },
    // 직진 배송 알림 타이틀, 직진 토글 버튼
    header: {
      headerTitle: {
        display: !config.hidden_flags?.option_dropdown && state.ui.is_restock_selection_mode,
        actions: {
          resetSelectionModeNotification: () => actions.resetRestockNotification(),
        },
      },
      shippingFilter,
    },
    subHeader: {
      isItemCardMode: state.ui.is_item_card_mode_active,
      isRestockNotificationMode: state.ui.is_restock_selection_mode,
      sizeRecommendation: productMetadata.size_recommendation,
    },
    // 입력형 옵션
    customInput,
    // 기본 옵션
    mainOptions,
    // 추가 옵션
    additionalOptions,
    // 아이템 카드
    itemCards,
    // 알림 문구
    noticeText,
    // 가격, 버튼
    footer: {
      display: isTimePassedTeasing && state.ui.is_item_card_mode_active,
      displayPriceSummary: !config?.hidden_flags?.price_summary,
      displayConfirmButton: !config?.hidden_flags?.confirm_button,
      priceSummary,
      confirmButton,
    },

    teasing: {
      display: !isTimePassedTeasing,
      date: formattedDate,
    },
  };
};
